import { useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { isEqual } from 'lodash';

import useAgGrid from 'app/hooks/useAgGrid';
import useQuickEdit from 'app/hooks/useQuickEdit';
import useColumns from 'app/hooks/useColumns';
import usePhotoSwipe from 'app/hooks/usePhotoSwipe';
import useDataSource from 'app/hooks/useDataSource';
import useActionsSelect from 'app/hooks/useActionsSelect';
import useFiltersButton from 'app/hooks/useFiltersButton';
import useDropdownSelect from 'app/hooks/useDropdownSelect';
import useGridState from 'app/hooks/useGridState';
import Cell from 'app/components/DataGrid/Cell';

const useDataGrid = props => {
  // props this hook takes
  const {
    getRowData,
    quickEditEnabled,
    onQuickAdd,
    onQuickEdit,
    columnOrder,
    onlyColumns,
    omitColumns,
    visibleColumns,
    onVisibleColumnsUpdate,
    onColumnOrderUpdate,
    columnDefOverrides,
    showEdit,
    showSelect,
    actions,
    showFilters,
    setPhotoSwipeItem,
    isMobile,
    dropDownOptions,
    onDropdownOptionChanged,
    showDropDown,
    getGridState,
    setGridState,
    resource,
    onDataGridLoad,
    onAdd,
    onEdit,
    defaultDropdownOption,
    newRowTemplate
  } = props;

  // get filterModel from redux when it updates
  const reduxParams = useSelector(state => state.params[resource]);
  const filterModel = reduxParams?.filter_model;
  const lastFilterModelRef = useRef(filterModel);

  // setup handles for AG Grid
  const agGrid = useAgGrid({
    filterModel,
    showFilters,
    isMobile
  });

  const showAdd = props.showAdd || !!props.onAdd;

  // setup quick edit mode
  const quickEdit = useQuickEdit({
    quickEditEnabled,
    onQuickAdd,
    onQuickEdit,
    newRowTemplate
  });

  // setup grid state
  const gridState = useGridState({
    gridState: props.gridState,
    getGridState,
    setGridState,
    visibleColumns,
    columnOrder,
    resource
  });

  // setup cellRenderer for columns.
  // it would be best to avoid having hooks depend on each other,
  // but in this case it seems unavoidable since a column's Cell component
  // will only have access to props passed to it by the colDef's cellRenderer,
  // and it will need access to the quickEdit hook.
  // defining the cellRenderer here and passing it as a prop seems ideal
  // in this case, since the dependency is at least easy to see
  const cellRenderer = ({ data, value = null, colDef }) => ((
    <Cell
      quickEdit={quickEdit}
      colDef={colDef}
      data={data}
      value={value}
    />
  ));

  // setup props from gridState for columns.
  // this also seems like an unavoidable dependency on the gridState hooked,
  // as described above for the cellRenderer.
  // defining the needed props here, so the dependency is at least clear
  const gridStateColumnOrder = gridState.columnOrder;
  const gridStateVisibleColumns = gridState.visibleColumns;
  const gridStatePinnedColumns = gridState.pinnedColumns;

  // setup columns
  const columns = useColumns({
    resource,
    filterModel,
    columnOrder,
    onlyColumns,
    omitColumns,
    visibleColumns,
    onVisibleColumnsUpdate,
    onColumnOrderUpdate,
    columnDefOverrides,
    showEdit,
    onEdit,
    showSelect,
    showFilters,
    isMobile,
    cellRenderer,
    gridStateColumnOrder,
    gridStateVisibleColumns,
    gridStatePinnedColumns
  });

  // setup photoswipe handler
  const photoSwipe = usePhotoSwipe({
    setPhotoSwipeItem
  });

  // setup server-side data source
  const dataSource = useDataSource({
    filterModel,
    getRowData,
    dropDownOptions,
    defaultDropdownOption
  });

  // setup hooks for UI components.
  // we've avoided passing hooks as props to other hooks,
  // but in this case these are simple hooks that just provide functionality
  // for UI components like selects and buttons, and they intentionally
  // pass hooks like agGrid to make them available to the component.
  // since circular dependencies aren't likely (other hooks don't need these),
  // passing hooks as dependencies seems fine here

  // setup actions select
  const actionsSelect = useActionsSelect({
    agGrid,
    dataSource,
    quickEdit,
    actions
  });

  const dropDownSelect = useDropdownSelect({
    agGrid,
    dataSource,
    quickEdit,
    onDropdownOptionChanged,
    dropDownOptions,
    showDropDown
  });

  // setup filters button
  const filtersButton = useFiltersButton({
    agGrid,
    quickEdit,
    columns,
    resource,
    showFilters
  });

  // determine when to show the loading icon
  const loading = dataSource.loading || gridState.loading;

  //-- data grid state and hook orchestra

  // refresh grid when filter model changes
  useEffect(() => {

    let sameFilters = isEqual(filterModel, lastFilterModelRef.current);

    if (!sameFilters) {
      lastFilterModelRef.current = filterModel;
      agGrid.setDataSource(dataSource.getDataSource);
    }

  }, [filterModel]);

  quickEdit.onActive(() => {
    agGrid.sizeColumnsToFit();
  });

  quickEdit.onInactive(() => {
    let columns = agGrid.getAllColumns()
      .filter(column => !column.colDef.suppressSizeToFit);

    agGrid.autoSizeColumns(columns);
  });

  quickEdit.onRowUpdate(updatedRow => {
    agGrid.updateRow(updatedRow);
  });

  columns.onVisibleColumnsUpdate(visibleColumns => {
    gridState.setVisibleColumns(visibleColumns);
  });

  dataSource.onRowData((page, rowData) => {
    let records = rowData.records || [];

    if (page === 1 && records.length) {
      columns.buildColumnDefs(records, agGrid.filtersActive);
    }

    // determine the current index of the last loaded row
    photoSwipe.setPhotoSwipeItems(records);
  });

  // dataGrid handles for outer components
  const refreshGrid = () => {
    // note: forceReload necessary to reload grid on creation/deletion
    // only doing so when filters aren't active since this breaks filter stacking
    if (!agGrid.filtersActive) {
      dataSource.forceReload();
    }

    agGrid.refreshGrid();
  }

  const refreshCells = () => {
    // A softer client-side refresh w/ change detection
    agGrid.refreshCells();
  }

  // setup callbacks for AgGrid
  const onGridReady = params => {
    agGrid.setGridApi(params.api);
    agGrid.setColumnApi(params.columnApi);

    if (dataSource.enabled) {
      agGrid.setDataSource(dataSource.getDataSource);
    }

    // pass dataGrid handles to the outer resource component (ex: Jobs)
    onDataGridLoad({
      refreshGrid,
      refreshCells,
      updateRow: agGrid.updateRow
    });
  }

  const onColumnMoved = event => {
    let {
      finished,
      column,
      toIndex
    } = event;

    // this event is fired multiple ways,
    // but we only care that a column has finished moving.
    // this event will also be called when a column has started moving
    // and when the grid options have been changed
    // (finished and column will be null, respectively)
    if (!finished || !column) {
      return;
    }

    let { field } = column.colDef;

    // get visible colDefs in the order they appear on the screen.
    let colDefs = agGrid.getRenderedColumnDefs();

    // update columnOrder server-side.
    // map colDef fields & remove the updated field from the list
    let params = colDefs.filter(colDef => colDef.field !== field)
      .map(colDef => colDef.field);

    // re-add the updated field at its new index
    params.splice(toIndex, 0, field);

    // save the new columnOrder server-side
    gridState.setColumnOrder(params);

    // update the state so that subsequent moves work correctly
    columns.setColumnOrder(params);
  }

  const onColumnVisible = event => {
    let { field } = event.column.colDef;
    columns.setColumnDefVisible(field, event.visible);
  }

  const onColumnPinned = event => {
    let {
      pinned,
      column: {
        colDef: { field }
      }
    } = event;

    // get pinned columns from grid state (excludes checkbox and action icons)
    let pinnedColumns = gridState.pinnedColumns || [];

    // remove field from array if it's there
    pinnedColumns = pinnedColumns.filter(f => f !== field);

    // if pinning then add the field to the array
    if (pinned) {
      pinnedColumns.push(field);
    }

    // save the new pinned columns server-side
    gridState.setPinnedColumns(pinnedColumns);

    // update the state so that the columnDef is pinned appropriately
    columns.setColumnDefPinned(field, pinned);
  }

  const toggleQuickEdit = () => {
    let activate = !quickEdit.active;

    if (activate) {
      quickEdit.activate();
    } else {
      quickEdit.discardChanges();
    }

    columns.toggleQuickEditActive(activate);
  }

  const commitQuickEditChanges = () => {
    quickEdit.commitChanges();
    columns.toggleQuickEditActive(false);
  }

  return {
    agGrid,
    showAdd,
    onAdd,
    onEdit,
    quickEdit,
    columns,
    dataSource,
    actionsSelect,
    dropDownSelect,
    filtersButton,
    gridState,
    loading,
    onGridReady,
    onColumnMoved,
    onColumnVisible,
    onColumnPinned,
    toggleQuickEdit,
    commitQuickEditChanges,
    photoSwipe
  }
}

export default useDataGrid;
